<script setup lang="ts">
import { ChevronDown, ChevronUp, GripHorizontal, X as CloseIcon } from '@lucide/vue'
import { computed, nextTick, onUnmounted, ref, useAttrs, watch } from 'vue'

defineOptions({ name: 'BaseToolbox', inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    title: string
    isCollapsed?: boolean
    closeable?: boolean
    closeDisabled?: boolean
    collapsible?: boolean
    panelClass?: string
    headerClass?: string
    bodyClass?: string
    initialOffsetX?: number
    initialOffsetY?: number
    draggable?: boolean
    placement?: 'floating' | 'bottom'
    edgeMargin?: number
    leftEdgeMargin?: number
    bottomOffset?: number | string
    bottomAlign?: 'stretch' | 'left' | 'center' | 'right'
    bottomMaxWidth?: number | string
    collapseOnHeaderClick?: boolean
    /**
     * Raise this toolbox above sibling toolboxes that share the default z-50.
     * Used for the active routing control panel so its Save/Cancel buttons stay
     * clickable when the Route profile toolbox overlaps it on desktop.
     */
    elevated?: boolean
  }>(),
  {
    isCollapsed: false,
    closeable: true,
    closeDisabled: false,
    collapsible: true,
    panelClass: '',
    headerClass: '',
    bodyClass: '',
    initialOffsetX: 0,
    initialOffsetY: 0,
    draggable: true,
    placement: 'floating',
    edgeMargin: 16,
    leftEdgeMargin: undefined,
    bottomOffset: undefined,
    bottomAlign: 'stretch',
    bottomMaxWidth: undefined,
    collapseOnHeaderClick: false,
    elevated: false,
  },
)

const emit = defineEmits<{
  close: []
  'update:isCollapsed': [value: boolean]
}>()

const attrs = useAttrs()
const panelRef = ref<HTMLElement | null>(null)
const position = ref<{ x: number; y: number } | null>(null)
let pendingPosition: { x: number; y: number } | null = null
let dragFrameId: number | null = null

let dragPointerId: number | null = null
let dragOffsetX = 0
let dragOffsetY = 0

const getOffsetParent = () =>
  (panelRef.value?.offsetParent as HTMLElement | null) ?? panelRef.value?.parentElement ?? null

const clampPosition = (nextPosition: { x: number; y: number }) => {
  if (props.placement !== 'floating') return nextPosition
  const panel = panelRef.value
  const parent = getOffsetParent()
  if (!panel || !parent) return nextPosition

  const margin = props.edgeMargin
  const maxX = Math.max(margin, parent.clientWidth - panel.offsetWidth - margin)
  const maxY = Math.max(margin, parent.clientHeight - panel.offsetHeight - margin)

  return {
    x: Math.max(margin, Math.min(nextPosition.x, maxX)),
    y: Math.max(margin, Math.min(nextPosition.y, maxY)),
  }
}

const initializePosition = async () => {
  if (!props.isOpen || position.value || props.placement !== 'floating') return

  await nextTick()

  const panel = panelRef.value
  const parent = getOffsetParent()
  if (!panel || !parent) return

  position.value = clampPosition({
    x: (parent.clientWidth - panel.offsetWidth) / 2 + props.initialOffsetX,
    y: (parent.clientHeight - panel.offsetHeight) / 2 + props.initialOffsetY,
  })
}

const stopDragging = () => {
  dragPointerId = null
  pendingPosition = null
  if (dragFrameId !== null) {
    window.cancelAnimationFrame(dragFrameId)
    dragFrameId = null
  }
  window.removeEventListener('pointermove', handlePointerMove)
  window.removeEventListener('pointerup', stopDragging)
  window.removeEventListener('pointercancel', stopDragging)
}

const flushPendingPosition = () => {
  dragFrameId = null
  if (!pendingPosition) return
  position.value = clampPosition(pendingPosition)
  pendingPosition = null
}

const handlePointerMove = (event: PointerEvent) => {
  const panel = panelRef.value
  const parent = getOffsetParent()
  if (!panel || !parent || dragPointerId !== event.pointerId) return

  const parentRect = parent.getBoundingClientRect()
  pendingPosition = {
    x: event.clientX - parentRect.left - dragOffsetX,
    y: event.clientY - parentRect.top - dragOffsetY,
  }

  if (dragFrameId === null) {
    dragFrameId = window.requestAnimationFrame(flushPendingPosition)
  }
}

const startDragging = (event: PointerEvent) => {
  if (!props.draggable || props.placement !== 'floating') return
  const panel = panelRef.value
  if (!panel) return

  event.preventDefault()
  dragPointerId = event.pointerId

  const panelRect = panel.getBoundingClientRect()
  dragOffsetX = event.clientX - panelRect.left
  dragOffsetY = event.clientY - panelRect.top

  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', stopDragging)
  window.addEventListener('pointercancel', stopDragging)
}

const toggleCollapsed = async () => {
  emit('update:isCollapsed', !props.isCollapsed)
  await nextTick()
  if (props.placement !== 'floating') return
  if (position.value) {
    position.value = clampPosition(position.value)
  }
}

const toggleCollapsedFromHeader = () => {
  if (!props.collapseOnHeaderClick || !props.collapsible) return
  void toggleCollapsed()
}

const normalizeCssLength = (value: number | string | undefined) =>
  typeof value === 'number' ? `${value}px` : value

const bottomPanelStyle = () => {
  const left = `${props.leftEdgeMargin ?? props.edgeMargin}px`
  const right = `${props.edgeMargin}px`
  const bottom = normalizeCssLength(props.bottomOffset) ?? `${props.edgeMargin}px`
  const maxWidth = normalizeCssLength(props.bottomMaxWidth)

  if (props.bottomAlign === 'stretch' || !maxWidth) {
    return {
      left,
      right,
      bottom,
      top: 'auto',
    }
  }

  const availableWidth = `calc(100% - ${left} - ${right})`
  const width = `min(${maxWidth}, ${availableWidth})`
  const baseStyle = {
    bottom,
    top: 'auto',
    width,
    maxWidth,
  }

  if (props.bottomAlign === 'center') {
    return {
      ...baseStyle,
      left,
      right,
      marginLeft: 'auto',
      marginRight: 'auto',
    }
  }

  if (props.bottomAlign === 'right') {
    return {
      ...baseStyle,
      left: 'auto',
      right,
    }
  }

  return {
    ...baseStyle,
    left,
    right: 'auto',
  }
}

const panelStyle = computed(() =>
  props.placement === 'bottom'
    ? bottomPanelStyle()
    : position.value
      ? { left: `${position.value.x}px`, top: `${position.value.y}px` }
      : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' },
)

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      position.value = null
      void initializePosition()
      return
    }

    stopDragging()
    position.value = null
  },
  { immediate: true },
)

watch(
  () => props.isCollapsed,
  () => {
    if (!props.isOpen) return
    void nextTick().then(() => {
      if (position.value) {
        position.value = clampPosition(position.value)
      } else if (props.placement === 'floating') {
        void initializePosition()
      }
    })
  },
)

onUnmounted(() => {
  stopDragging()
})
</script>

<template>
  <div
    v-if="props.isOpen"
    v-bind="attrs"
    ref="panelRef"
    :class="[
      'pointer-events-auto absolute rounded-xl border border-gray-200 bg-white/96 shadow-xl',
      props.elevated ? 'z-51' : 'z-50',
      props.placement === 'floating' ? 'w-[320px] max-w-[calc(100%-2rem)]' : '',
      props.panelClass,
    ]"
    :style="panelStyle"
    @click.stop
    @pointerdown.stop
  >
    <div
      :class="[
        'flex items-center justify-between rounded-t-xl border-b border-gray-200 bg-gray-50 px-4 py-3 select-none touch-none',
        props.draggable && props.placement === 'floating'
          ? 'cursor-move'
          : props.collapseOnHeaderClick && props.collapsible
            ? 'cursor-pointer'
            : 'cursor-default',
        props.headerClass,
      ]"
      @pointerdown="startDragging"
      @click="toggleCollapsedFromHeader"
    >
      <div class="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <GripHorizontal
          v-if="props.draggable && props.placement === 'floating'"
          class="h-4 w-4 text-gray-500"
        />
        <slot name="title">
          <span>{{ props.title }}</span>
        </slot>
      </div>
      <div class="flex items-center gap-1">
        <button
          v-if="props.collapsible"
          type="button"
          class="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
          :aria-expanded="!props.isCollapsed"
          :aria-label="props.isCollapsed ? 'Expand toolbox' : 'Collapse toolbox'"
          @pointerdown.stop
          @click.stop="toggleCollapsed"
        >
          <ChevronDown v-if="props.isCollapsed" class="h-4 w-4" />
          <ChevronUp v-else class="h-4 w-4" />
        </button>
        <button
          v-if="props.closeable"
          type="button"
          class="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
          aria-label="Close toolbox"
          :disabled="props.closeDisabled"
          @pointerdown.stop
          @click.stop="emit('close')"
        >
          <CloseIcon class="h-4 w-4" />
        </button>
      </div>
    </div>

    <div v-if="!props.isCollapsed" :class="['space-y-5 px-4 py-4', props.bodyClass]">
      <slot />
    </div>
  </div>
</template>
